import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { Heart, Gift, Sparkles } from "lucide-react";
import GoogleAuthHelper from "@/components/GoogleAuthHelper";

export default function Signup() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const refCode = searchParams.get("ref") || "";
  const [referrerName, setReferrerName] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (refCode) {
      // Persist for Google OAuth round-trip
      try { sessionStorage.setItem("hc_ref", refCode); } catch (e) {}
      api.get(`/referral/lookup/${encodeURIComponent(refCode)}`)
        .then((r) => setReferrerName(r.data.referrer_name))
        .catch(() => { /* invalid ref code, just hide banner */ });
    }
  }, [refCode]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const newUser = await register(email, password, name, refCode || null, phone || null);
      if (newUser.referral_premium_until) {
        toast.success("🎁 Welcome! You just got 30 days of Premium free.");
      } else {
        toast.success("Welcome! Your 14-day Premium trial is active.");
      }
      nav("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create account");
    } finally {
      setBusy(false);
    }
  };

  const googleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex bg-[#FCFAF8] relative overflow-hidden">
      <div className="hc-blob" style={{ background: "#E8DCC8", width: 400, height: 400, top: -100, left: -100 }} />
      <div className="hc-blob" style={{ background: "#D8E2D8", width: 360, height: 360, bottom: -100, right: -100 }} />

      <div className="relative z-10 w-full max-w-md mx-auto px-6 py-12 flex flex-col justify-center">
        <Link to="/" className="flex items-center gap-2 mb-12" data-testid="signup-back-home">
          <img src="/hope-chest-logo.png" alt="Hope Chest" className="w-10 h-10 object-contain" />
          <span className="font-brand text-xl font-semibold">Hope Chest</span>
        </Link>

        {referrerName && (
          <div className="mb-6 p-4 rounded-2xl bg-[#E8DCC8] border border-[#D9A441]/30 flex items-start gap-3" data-testid="referral-banner">
            <Gift className="w-5 h-5 text-[#3A5A4A] mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-[#3D3935]">
                {referrerName} invited you to Hope Chest.
              </p>
              <p className="text-xs text-[#7A736E] mt-0.5">
                Sign up below and you&apos;ll both get <strong>30 days of Premium free</strong>.
              </p>
            </div>
          </div>
        )}

        <h1 className="font-display text-3xl md:text-4xl font-semibold mb-2">Begin your Hope Chest.</h1>
        <p className="text-[#7A736E] mb-2">It takes less than a minute. Free forever for one loved one.</p>
        <p className="text-sm mb-6 inline-flex items-center gap-1.5 rounded-full bg-[#E8DCC8] text-[#3A5A4A] px-3 py-1.5 font-medium w-fit" data-testid="signup-trial-pill">
          <Sparkles className="w-3.5 h-3.5" /> 14 days of Premium free — no credit card needed
        </p>

        <Button
          onClick={googleSignIn}
          variant="outline"
          className="w-full rounded-full bg-white border-[#EBE5DF] hover:bg-[#F3EFEA] py-6 mb-4"
          data-testid="signup-google-button"
        >
          <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
          Continue with Google
        </Button>
        <GoogleAuthHelper />

        <div className="flex items-center gap-4 my-6">
          <div className="flex-1 h-px bg-[#EBE5DF]" />
          <span className="text-xs text-[#7A736E] uppercase tracking-wider">or with email</span>
          <div className="flex-1 h-px bg-[#EBE5DF]" />
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label htmlFor="name" className="text-sm">Your name</Label>
            <Input id="name" required value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5 rounded-xl border-[#EBE5DF] py-6" data-testid="signup-name-input" />
          </div>
          <div>
            <Label htmlFor="email" className="text-sm">Email</Label>
            <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5 rounded-xl border-[#EBE5DF] py-6" data-testid="signup-email-input" />
          </div>
          <div>
            <Label htmlFor="password" className="text-sm">Password</Label>
            <Input id="password" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1.5 rounded-xl border-[#EBE5DF] py-6" data-testid="signup-password-input" />
          </div>
          <div>
            <Label htmlFor="phone" className="text-sm">Mobile phone <span className="text-[#7A736E] font-normal">(optional — add it later in Settings)</span></Label>
            <Input
              id="phone"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="(555) 123-4567"
              className="mt-1.5 rounded-xl border-[#EBE5DF] py-6"
              data-testid="signup-phone-input"
            />
            <p className="text-xs text-[#7A736E] mt-1.5">For SMS reminders on Premium. You&apos;ll verify it later — no texts until you opt in.</p>
          </div>
          <Button type="submit" disabled={busy} className="w-full rounded-full bg-[#3A5A4A] hover:bg-[#2C4538] py-6" data-testid="signup-submit-button">
            {busy ? "Creating…" : "Create my Hope Chest"}
          </Button>
        </form>

        <p className="mt-8 text-sm text-center text-[#7A736E]">
          Already have an account? <Link to="/login" className="text-[#3A5A4A] font-medium hover:underline" data-testid="signup-to-login">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
